import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Clock, BookOpen, BarChart3, Dumbbell, Volume2, Play, Pause, Square, Sparkles, CheckCircle, RotateCcw, Star, Loader2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function ExerciseDialog({
  open, onOpenChange, exercise,
  previousSessions, exerciseNotes, setExerciseNotes,
  // TTS
  isSpeaking, isPaused, currentStep, isGenerating,
  speakInstructions, stopSpeech, togglePauseSpeech,
  // Guided Practice
  guidedMode, guidedStep, guidedTimer, guidedPaused, guidedComplete,
  startGuidedPractice, stopGuidedPractice, toggleGuidedPause, getStepDuration,
  // Callbacks
  onCompleteSession
}) {
  const navigate = useNavigate();
  if (!exercise) return null;

  const instructions = exercise.instructions;
  const isArray = Array.isArray(instructions);

  return (
    <Dialog open={open} onOpenChange={(o) => {
      if (!o) { stopSpeech(); stopGuidedPractice(); }
      onOpenChange(o);
    }}>
      <DialogContent data-testid="exercise-dialog" className="max-w-2xl max-h-[85vh] overflow-y-auto p-0" aria-describedby={undefined}>
        <DialogHeader className="sr-only">
          <DialogTitle>{exercise.title}</DialogTitle>
        </DialogHeader>

        {/* Header */}
        <div className="bg-gradient-to-br from-emerald-600 to-teal-700 p-6 text-white">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                  exercise.difficulty === 'beginner' ? 'bg-green-400/20 text-green-100' :
                  exercise.difficulty === 'intermediate' ? 'bg-amber-400/20 text-amber-100' :
                  'bg-red-400/20 text-red-100'
                }`}>{exercise.difficulty || 'All Levels'}</span>
                <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-white/15 text-white/90">
                  {exercise.type || exercise.category}
                </span>
              </div>
              <h2 className="text-xl font-bold mb-2">{exercise.title}</h2>
              <p className="text-sm text-emerald-100 leading-relaxed">{exercise.description}</p>
            </div>
          </div>
          <div className="flex items-center gap-4 mt-4 pt-4 border-t border-white/15">
            <div className="flex items-center gap-1.5 text-sm text-emerald-100">
              <Clock className="w-4 h-4" />
              <span className="font-semibold text-white">{exercise.duration_minutes} min</span>
            </div>
            {isArray && (
              <div className="flex items-center gap-1.5 text-sm text-emerald-100">
                <BookOpen className="w-4 h-4" />
                <span className="font-semibold text-white">{instructions.length} steps</span>
              </div>
            )}
            {previousSessions.length > 0 && (
              <div className="flex items-center gap-1.5 text-sm text-emerald-100">
                <BarChart3 className="w-4 h-4" />
                <span className="font-semibold text-white">{previousSessions.length} sessions</span>
              </div>
            )}
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Guided Practice Mode */}
          {guidedMode && isArray && (
            <div data-testid="guided-practice-panel" className="rounded-2xl overflow-hidden border-2 border-emerald-400 shadow-lg shadow-emerald-500/10">
              <div className="bg-gradient-to-r from-emerald-600 to-teal-600 px-5 py-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="relative flex h-2.5 w-2.5">
                    {!guidedPaused && !guidedComplete && <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />}
                    <span className={`relative inline-flex rounded-full h-2.5 w-2.5 ${guidedComplete ? 'bg-amber-300' : guidedPaused ? 'bg-amber-400' : 'bg-white'}`} />
                  </span>
                  <span className="text-white text-sm font-bold tracking-wide">
                    {guidedComplete ? 'Session Complete' : guidedPaused ? 'Paused' : 'Guided Practice'}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {!guidedComplete && (
                    <>
                      <button data-testid="guided-pause-btn" onClick={toggleGuidedPause} className="p-1.5 rounded-full bg-white/20 hover:bg-white/30 text-white transition-colors">
                        {guidedPaused ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
                      </button>
                      <button data-testid="guided-stop-btn" onClick={stopGuidedPractice} className="p-1.5 rounded-full bg-white/20 hover:bg-white/30 text-white transition-colors">
                        <Square className="w-4 h-4" />
                      </button>
                    </>
                  )}
                  {guidedComplete && (
                    <button onClick={stopGuidedPractice} className="text-xs text-white/80 hover:text-white font-semibold">Close</button>
                  )}
                </div>
              </div>

              {guidedComplete ? (
                <div className="bg-gradient-to-br from-emerald-50 to-teal-50 p-8 text-center">
                  <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-9 h-9 text-emerald-600" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-1">Well Done!</h3>
                  <p className="text-sm text-gray-500 mb-5">You completed all {instructions.length} steps.</p>
                  <div className="flex gap-3 justify-center">
                    <Button size="sm" variant="outline" onClick={stopGuidedPractice} className="text-xs">Exit</Button>
                    <Button size="sm" onClick={() => startGuidedPractice(instructions)} className="bg-emerald-600 hover:bg-emerald-700 text-xs">
                      <RotateCcw className="w-3.5 h-3.5 mr-1" />Restart
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="bg-white p-5">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Step {guidedStep + 1} of {instructions.length}</span>
                    <span className="text-xs font-mono font-bold text-emerald-600">{Math.floor(guidedTimer / 60)}:{String(guidedTimer % 60).padStart(2, '0')}</span>
                  </div>
                  <div className="flex gap-1 mb-5">
                    {instructions.map((_, i) => (
                      <div key={i} className="flex-1 h-1.5 rounded-full overflow-hidden bg-gray-100">
                        <div className={`h-full rounded-full transition-all duration-500 ${
                          i < guidedStep ? 'bg-emerald-500' : i === guidedStep ? 'bg-emerald-400' : ''
                        }`} style={i === guidedStep ? {
                          width: `${Math.max(5, ((getStepDuration() - guidedTimer) / getStepDuration()) * 100)}%`,
                          transition: 'width 1s linear'
                        } : i < guidedStep ? { width: '100%' } : { width: '0%' }} />
                      </div>
                    ))}
                  </div>
                  <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-5 border border-emerald-100 min-h-[100px] flex items-center">
                    <div className="flex items-start gap-4">
                      <span className="flex-shrink-0 w-10 h-10 rounded-xl bg-emerald-600 text-white flex items-center justify-center text-lg font-black shadow-md">{guidedStep + 1}</span>
                      <p className="text-base text-gray-800 leading-relaxed font-medium pt-1.5">{instructions[guidedStep]}</p>
                    </div>
                  </div>
                  <div className="flex justify-center mt-5">
                    <div className="relative w-20 h-20">
                      <svg className="w-20 h-20 -rotate-90" viewBox="0 0 80 80">
                        <circle cx="40" cy="40" r="34" stroke="#e5e7eb" strokeWidth="5" fill="none" />
                        <circle cx="40" cy="40" r="34" stroke="url(#guidedTimerGrad)" strokeWidth="5" fill="none"
                          strokeDasharray={`${(guidedTimer / getStepDuration()) * 213.6} 213.6`}
                          strokeLinecap="round" className="transition-all duration-1000 ease-linear" />
                        <defs><linearGradient id="guidedTimerGrad" x1="0%" y1="0%" x2="100%" y2="0%"><stop offset="0%" stopColor="#34d399" /><stop offset="100%" stopColor="#2dd4bf" /></linearGradient></defs>
                      </svg>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="text-lg font-black text-emerald-700 font-mono">{guidedTimer}</span>
                      </div>
                    </div>
                  </div>
                  {guidedStep + 1 < instructions.length && (
                    <div className="mt-4 px-3 py-2 rounded-lg bg-gray-50 border border-gray-100">
                      <p className="text-[10px] text-gray-400 uppercase tracking-wider font-bold mb-0.5">Up Next</p>
                      <p className="text-xs text-gray-500 line-clamp-1">{instructions[guidedStep + 1]}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Instructions with TTS */}
          {!guidedMode && (
            <div className="bg-gradient-to-br from-slate-50 to-emerald-50/40 rounded-2xl border border-emerald-100 overflow-hidden">
              <div className="flex items-center justify-between px-5 py-3 bg-white border-b border-emerald-100">
                <h3 className="font-bold text-gray-900 flex items-center gap-2">
                  <Dumbbell className="w-4 h-4 text-emerald-600" />
                  Step-by-Step Instructions
                </h3>
                {isArray && (
                  <div data-testid="tts-controls" className="flex items-center gap-1.5">
                    {!isSpeaking && !isGenerating ? (
                      <button data-testid="tts-play-btn" onClick={() => speakInstructions(instructions)}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold transition-colors shadow-sm">
                        <Volume2 className="w-3.5 h-3.5" />Listen
                      </button>
                    ) : isGenerating ? (
                      <div className="flex items-center gap-1.5">
                        <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-500/80 text-white text-xs font-semibold">
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />Preparing voice...
                        </span>
                        <button data-testid="tts-stop-btn" onClick={stopSpeech}
                          className="p-1.5 rounded-full bg-rose-500 hover:bg-rose-600 text-white transition-colors">
                          <Square className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ) : (
                      <>
                        <button data-testid="tts-pause-btn" onClick={togglePauseSpeech}
                          className={`p-1.5 rounded-full transition-colors ${isPaused ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'bg-amber-500 hover:bg-amber-600 text-white'}`}>
                          {isPaused ? <Play className="w-3.5 h-3.5" /> : <Pause className="w-3.5 h-3.5" />}
                        </button>
                        <button data-testid="tts-stop-btn" onClick={stopSpeech}
                          className="p-1.5 rounded-full bg-rose-500 hover:bg-rose-600 text-white transition-colors">
                          <Square className="w-3.5 h-3.5" />
                        </button>
                        {isSpeaking && !isPaused && (
                          <span className="ml-1 flex gap-0.5">
                            {[0, 1, 2].map(i => (
                              <span key={i} className="w-1 bg-emerald-500 rounded-full animate-pulse"
                                style={{ height: `${8 + (i % 2) * 6}px`, animationDelay: `${i * 0.15}s` }} />
                            ))}
                          </span>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
              <div className="px-5 py-4">
                {isArray ? (
                  <ol className="space-y-3">
                    {instructions.map((instruction, index) => (
                      <li key={index} data-testid={`instruction-step-${index}`}
                        className={`flex items-start gap-3 p-3 rounded-xl transition-all duration-300 ${
                          currentStep === index ? 'bg-emerald-100 border border-emerald-300 shadow-sm' : 'hover:bg-white/60'
                        }`}>
                        <span className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${
                          currentStep === index ? 'bg-emerald-600 text-white shadow-md' : 'bg-emerald-100 text-emerald-700'
                        }`}>{index + 1}</span>
                        <span className={`text-sm leading-relaxed pt-0.5 ${currentStep === index ? 'text-emerald-900 font-medium' : 'text-gray-700'}`}>
                          {instruction}
                        </span>
                      </li>
                    ))}
                  </ol>
                ) : (
                  <p className="text-sm text-gray-700 whitespace-pre-line leading-relaxed">{instructions || "No instructions available."}</p>
                )}
              </div>
            </div>
          )}

          {/* Previous Sessions */}
          {previousSessions.length > 0 && (
            <div className="rounded-2xl border border-purple-100 overflow-hidden">
              <div className="flex items-center justify-between px-5 py-3 bg-purple-50 border-b border-purple-100">
                <h3 className="font-bold text-gray-900 flex items-center gap-2">
                  <BarChart3 className="w-4 h-4 text-purple-600" />
                  Previous Sessions ({previousSessions.length})
                </h3>
              </div>
              <div className="px-5 py-4 space-y-2 max-h-48 overflow-y-auto">
                {previousSessions.slice(0, 5).map((session, index) => (
                  <div key={session.id || index} className="flex items-start gap-3 p-3 rounded-xl bg-gray-50 hover:bg-purple-50/50 transition-colors">
                    <div className="text-center flex-shrink-0 w-12">
                      <p className="text-[10px] text-gray-500 uppercase">{new Date(session.date).toLocaleDateString('en-US', { weekday: 'short' })}</p>
                      <p className="text-xs font-bold text-gray-700">{new Date(session.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</p>
                    </div>
                    <p className="text-sm text-gray-600 leading-relaxed">{session.notes || <span className="italic text-gray-400">No notes recorded</span>}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3">
            {!guidedMode && isArray && (
              <Button data-testid="guided-practice-btn" onClick={() => startGuidedPractice(instructions)}
                className="flex-1 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-sm font-semibold shadow-sm">
                <Sparkles className="w-4 h-4 mr-1.5" />Guided Practice
              </Button>
            )}
            <Button data-testid="complete-session-btn" onClick={onCompleteSession}
              className="flex-1 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-sm font-semibold shadow-sm">
              <CheckCircle className="w-4 h-4 mr-1.5" />Complete Session
            </Button>
            {!guidedMode && null}
          </div>

          {/* Personal Notes */}
          <div className="rounded-2xl border border-amber-100 overflow-hidden">
            <div className="flex items-center px-5 py-3 bg-amber-50 border-b border-amber-100">
              <h3 className="font-bold text-gray-900 flex items-center gap-2">
                <Star className="w-4 h-4 text-amber-600" />Personal Notes
              </h3>
            </div>
            <div className="p-5">
              <p className="text-xs text-gray-500 mb-3">Your notes are saved permanently and shown each time you open this exercise.</p>
              <Textarea
                value={exerciseNotes[exercise?.id] || ""}
                onChange={(e) => {
                  if (exercise) setExerciseNotes(prev => ({ ...prev, [exercise.id]: e.target.value }));
                }}
                placeholder="Add your personal tips, reminders, or insights..."
                className="bg-white text-sm"
                rows={3}
              />
              <div className="flex justify-end mt-3">
                <Button onClick={async () => {
                  if (!exercise) return;
                  try {
                    const token = localStorage.getItem('token');
                    await axios.post(`${API}/training/exercises/notes`, {
                      exercise_id: exercise.id, exercise_title: exercise.title,
                      notes: exerciseNotes[exercise.id] || ""
                    }, { headers: { Authorization: `Bearer ${token}` } });
                    toast.success("Personal notes saved!");
                  } catch { toast.error("Failed to save notes"); }
                }} size="sm" className="bg-amber-600 hover:bg-amber-700 text-xs font-semibold">
                  <CheckCircle className="w-3.5 h-3.5 mr-1" />Save Notes
                </Button>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
